import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import type { PackageData } from '@/pages/CreatePackage';
import { Plus, Clock, MapPin, Calendar, Users, Plane, DollarSign, FileText, PlaneTakeoff, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAirlines } from '@/hooks/useTasks';
import { useCountries } from '@/hooks/useCountries';
import { useCities } from '@/hooks/useCities';
type Flight = PackageData['flights'][0];
interface AddFlightFormProps {
  onAddFlight: (flight: Flight) => void;
}
const initialFlightState: Omit<Flight, 'price' | 'costPrice' | 'sellingPrice'> & {
  price: string;
  costPrice: string;
  sellingPrice: string;
} = {
  type: 'outbound',
  from: '',
  to: '',
  fromCountry: '',
  fromCity: '',
  toCountry: '',
  toCity: '',
  date: '',
  time: '',
  airline: '',
  class: 'Economy',
  price: '',
  costPrice: '',
  sellingPrice: '',
  adults: 1,
  children: 0,
  infants: 0,
  transitFlight: false,
  transitDuration: 0,
  supplier: '',
  notes: ''
};
export const AddFlightForm = ({
  onAddFlight
}: AddFlightFormProps) => {
  const [flight, setFlight] = useState(initialFlightState);
  const {
    airlines,
    isLoading: isLoadingAirlines
  } = useAirlines();
  const {
    data: countries = []
  } = useCountries();
  const {
    data: cities = []
  } = useCities();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFlight(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSelectChange = (name: keyof typeof flight) => (value: string) => {
    setFlight(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleNumberChange = (name: 'adults' | 'children' | 'infants') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || 0;
    setFlight(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const calculateTransitDuration = () => {
    if (flight.date && flight.time && flight.transitFlight) {
      // This is a simplified calculation - in reality you'd need departure and arrival times
      // For now, just show that the field gets updated
      setFlight(prev => ({
        ...prev,
        transitDuration: 2
      })); // 2 hours default
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!flight.from || !flight.to || !flight.date || !flight.time || !flight.airline || !flight.sellingPrice) {
      toast.error("الرجاء تعبئة جميع الحقول المطلوبة.");
      return;
    }
    onAddFlight({
      ...flight,
      type: flight.type as 'outbound' | 'return' | 'internal',
      class: flight.class,
      price: parseFloat(flight.sellingPrice) || 0,
      costPrice: parseFloat(flight.costPrice) || 0,
      sellingPrice: parseFloat(flight.sellingPrice) || 0,
      transitDuration: flight.transitFlight ? flight.transitDuration : undefined
    });
    setFlight(initialFlightState);
  };
  const filteredFromCities = cities.filter(city => city.country_id === flight.fromCountry);
  const filteredToCities = cities.filter(city => city.country_id === flight.toCountry);
  return <Card className="bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-blue-100">
      
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Flight Info Section */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Plane className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">معلومات الرحلة الأساسية</h3>
              <div className="flex-1 h-px bg-gradient-to-r from-blue-200 to-transparent"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="type" className="flex items-center gap-2">
                  <PlaneTakeoff className="w-4 h-4 text-blue-500" />
                  نوع الرحلة
                </Label>
                <Select name="type" value={flight.type} onValueChange={handleSelectChange('type')}>
                  <SelectTrigger className="h-12 border-2 focus:border-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outbound">✈️ رحلة ذهاب</SelectItem>
                    <SelectItem value="return">🛬 رحلة عودة</SelectItem>
                    <SelectItem value="internal">🔄 رحلة داخلية</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="airline" className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-green-500" />
                  شركة الطيران
                </Label>
                <Select name="airline" value={flight.airline} onValueChange={handleSelectChange('airline')}>
                  <SelectTrigger className="h-12 border-2 focus:border-blue-500" disabled={isLoadingAirlines}>
                    <SelectValue placeholder="اختر شركة طيران" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingAirlines ? <SelectItem value="loading" disabled>جاري التحميل...</SelectItem> : airlines && airlines.length > 0 ? airlines.map(a => <SelectItem key={a.id} value={a.name_ar}>
                          {a.name_ar} ({a.code})
                        </SelectItem>) : <SelectItem value="no-airlines" disabled>لا توجد شركات طيران</SelectItem>}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="class" className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-500 rounded"></div>
                  درجة السفر
                </Label>
                <Select name="class" value={flight.class} onValueChange={handleSelectChange('class')}>
                  <SelectTrigger className="h-12 border-2 focus:border-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Economy">🎫 درجة سياحية</SelectItem>
                    <SelectItem value="Business">💼 درجة رجال أعمال</SelectItem>
                    <SelectItem value="First">👑 الدرجة الأولى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Origin Details Section */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl shadow-sm border border-green-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-500 rounded-lg">
                <PlaneTakeoff className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">تفاصيل المغادرة</h3>
              <div className="flex-1 h-px bg-gradient-to-r from-green-200 to-transparent"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="fromCountry" className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-green-600" />
                  دولة المغادرة
                </Label>
                <Select value={flight.fromCountry} onValueChange={handleSelectChange('fromCountry')}>
                  <SelectTrigger className="h-12 border-2 focus:border-green-500 bg-white">
                    <SelectValue placeholder="اختر الدولة" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(country => <SelectItem key={country.id} value={country.id}>
                        {country.name_ar}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="fromCity" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-green-600" />
                  مدينة المغادرة
                </Label>
                <Select value={flight.fromCity} onValueChange={handleSelectChange('fromCity')}>
                  <SelectTrigger className="h-12 border-2 focus:border-green-500 bg-white">
                    <SelectValue placeholder="اختر المدينة" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredFromCities.map(city => <SelectItem key={city.id} value={city.id}>
                        {city.name_ar}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="from" className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-green-600" />
                  مطار المغادرة
                </Label>
                <Input id="from" name="from" value={flight.from} onChange={handleChange} placeholder="مثال: RUH" className="h-12 border-2 focus:border-green-500 bg-white" required />
              </div>
            </div>
          </div>

          {/* Destination Details Section */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 rounded-xl shadow-sm border border-orange-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-orange-500 rounded-lg">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">تفاصيل الوصول</h3>
              <div className="flex-1 h-px bg-gradient-to-r from-orange-200 to-transparent"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="toCountry" className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-orange-600" />
                  دولة الوصول
                </Label>
                <Select value={flight.toCountry} onValueChange={handleSelectChange('toCountry')}>
                  <SelectTrigger className="h-12 border-2 focus:border-orange-500 bg-white">
                    <SelectValue placeholder="اختر الدولة" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map(country => <SelectItem key={country.id} value={country.id}>
                        {country.name_ar}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="toCity" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-orange-600" />
                  مدينة الوصول
                </Label>
                <Select value={flight.toCity} onValueChange={handleSelectChange('toCity')}>
                  <SelectTrigger className="h-12 border-2 focus:border-orange-500 bg-white">
                    <SelectValue placeholder="اختر المدينة" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredToCities.map(city => <SelectItem key={city.id} value={city.id}>
                        {city.name_ar}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="to" className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-orange-600" />
                  مطار الوصول
                </Label>
                <Input id="to" name="to" value={flight.to} onChange={handleChange} placeholder="مثال: DXB" className="h-12 border-2 focus:border-orange-500 bg-white" required />
              </div>
            </div>
          </div>

          {/* Date, Time and Passengers Section */}
          <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-6 rounded-xl shadow-sm border border-purple-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">التوقيت والمسافرون</h3>
              <div className="flex-1 h-px bg-gradient-to-r from-purple-200 to-transparent"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="space-y-2">
                <Label htmlFor="date" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  التاريخ
                </Label>
                <Input id="date" name="date" type="date" value={flight.date} onChange={handleChange} onBlur={calculateTransitDuration} className="h-12 border-2 focus:border-purple-500 bg-white" required />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="time" className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-600" />
                  الوقت
                </Label>
                <Input id="time" name="time" type="time" value={flight.time} onChange={handleChange} onBlur={calculateTransitDuration} className="h-12 border-2 focus:border-purple-500 bg-white" required />
              </div>

              <div className="space-y-2">
                <Label htmlFor="adults" className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  البالغين
                </Label>
                <Input id="adults" name="adults" type="number" min="1" value={flight.adults} onChange={handleNumberChange('adults')} className="h-12 border-2 focus:border-purple-500 bg-white" required />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="children" className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  الأطفال
                </Label>
                <Input id="children" name="children" type="number" min="0" value={flight.children} onChange={handleNumberChange('children')} className="h-12 border-2 focus:border-purple-500 bg-white" />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="infants" className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  الرضع
                </Label>
                <Input id="infants" name="infants" type="number" min="0" value={flight.infants} onChange={handleNumberChange('infants')} className="h-12 border-2 focus:border-purple-500 bg-white" />
              </div>
            </div>
          </div>

          {/* Transit and Additional Info Section */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="space-y-6">
              {/* Transit Flight */}
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex items-center space-x-2 space-x-reverse mb-4">
                  <Checkbox id="transitFlight" checked={flight.transitFlight} onCheckedChange={checked => setFlight(prev => ({
                  ...prev,
                  transitFlight: checked as boolean
                }))} />
                  <Label htmlFor="transitFlight" className="flex items-center gap-2 font-medium">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    رحلة ترانزيت (انتظار)
                  </Label>
                </div>

                {flight.transitFlight && <div className="space-y-2">
                    <Label htmlFor="transitDuration" className="text-sm">مدة الانتظار (ساعات)</Label>
                    <Input id="transitDuration" name="transitDuration" type="number" min="0" step="0.5" value={flight.transitDuration} onChange={e => setFlight(prev => ({
                  ...prev,
                  transitDuration: parseFloat(e.target.value) || 0
                }))} placeholder="يحسب تلقائياً" className="h-10 bg-white border-yellow-300 focus:border-yellow-500" />
                  </div>}
              </div>

              {/* Supplier */}
              <div className="space-y-2">
                <Label htmlFor="supplier" className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-600" />
                  المورد
                </Label>
                <Input id="supplier" name="supplier" value={flight.supplier} onChange={handleChange} placeholder="اسم المورد" className="h-12 border-2 focus:border-blue-500" />
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-6 rounded-xl shadow-sm border border-emerald-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-500 rounded-lg">
                <DollarSign className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">التسعير</h3>
              <div className="flex-1 h-px bg-gradient-to-r from-emerald-200 to-transparent"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="costPrice" className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  سعر التكلفة (SAR)
                </Label>
                <Input id="costPrice" name="costPrice" type="number" min="0" step="0.01" value={flight.costPrice} onChange={handleChange} placeholder="0.00" className="h-12 border-2 focus:border-emerald-500 bg-white" />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sellingPrice" className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  سعر البيع (SAR)
                </Label>
                <Input id="sellingPrice" name="sellingPrice" type="number" min="0" step="0.01" value={flight.sellingPrice} onChange={handleChange} placeholder="0.00" className="h-12 border-2 focus:border-emerald-500 bg-white" required />
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="bg-gray-50 p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-gray-500 rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">ملاحظات إضافية</h3>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">ملاحظات الموظف</Label>
              <Textarea id="notes" name="notes" value={flight.notes} onChange={handleChange} placeholder="أدخل أي ملاحظات إضافية..." rows={4} className="border-2 focus:border-gray-400 bg-white resize-none" />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <Button type="submit" className="h-12 px-8 bg-gradient-to-r from-blue-500 to-sky-500 hover:from-blue-600 hover:to-sky-600 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
              <Plus className="w-5 h-5 ml-2" />
              إضافة الرحلة
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>;
};